// External libraries
import React, { useEffect, useRef, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useQuery } from "@tanstack/react-query";

// Components
import { Body, BodyColor, BodyType, Heading, TypographyColor, TypographyType } from "../../ui-elements"
import { BrandWatchList, CifTypeSwitch, PresenceConfig, POICategoryListHome, TopDistricts } from ".";
import { AggLayerPopup } from "./AggLayerPopup";

// Utilities
import { brandList, CIFType, cifTypeState, errorState, expandedPresenceConfigState, filterDefaultState, filterLoadingState, filtersState, geoState, LegendGeoLevel, loggedUserState, metricState, overlayCountState, POICategories, poiMapColorState, prevCifTypeState, selectedBrands, selectedCategoryState, selectedTabState, selPOICategories, selAggLayerState, topFiveRegionsState, hexLoadingState } from "../../states";
import { useCIFService, useMapsService } from "../../services";
import { OverlayMetricPopup } from "./OverlayMetricPopup";
import { getCurrGeoLevelName, subGeoLevel, getParentGeoId, getLayerInfoText, cleanFilterTree } from "../../helpers";
import { BOUNDARY_TYPES, DATA_TABS, MetricKey, RouteConstants } from "../../constants";
import { useLocation, useSearchParams } from "react-router-dom";

// CSS
import styles from './LeftSideBarNew.module.css';
import { FilterRegionModal } from "../header";

interface LeftSideBarNewProps {
    isChecked: { MBP: boolean, spotlight: boolean, poimarker: boolean, subregionBoundaries: boolean, adjacency: boolean, catchments: boolean, };
    toggleSwitch: (e: any, name?: string, checked?: boolean) => void;
    presenceTypeCount: any[]
    legendShimmer: boolean;
    fetchMyPresence: (payload: any) => void;
    setMbpFilter: React.Dispatch<React.SetStateAction<any>>;
    setMapSpinner: React.Dispatch<React.SetStateAction<number>>;
    mapSpinner: number,
    myPresence: { info: any[], markers: any[] };
    isPoiOverlapped: boolean;
    geoCode: number;
    catchmentTypes: any;
    isCatchmentAvailable: boolean;
    selCatchmentType: any;
    setSelCatchmentType: React.Dispatch<React.SetStateAction<any>>;
    selSubRegion: any;
    setSelSubRegion: React.Dispatch<React.SetStateAction<any>>;
    catchment: any;
    layersData: any[]; // New data structure with mapping and items
    selectedDataTab: string; // 'choropleth' or 'markers'
    setSelectedDataTab: React.Dispatch<React.SetStateAction<string>>;
}

export const LeftSideBarNew: React.FC<LeftSideBarNewProps> = ({
    isChecked,
    toggleSwitch,
    presenceTypeCount,
    fetchMyPresence,
    legendShimmer,
    setMbpFilter,
    setMapSpinner,
    mapSpinner,
    myPresence,
    isPoiOverlapped,
    geoCode,
    catchmentTypes,
    isCatchmentAvailable,
    selCatchmentType,
    setSelCatchmentType,
    selSubRegion,
    setSelSubRegion,
    catchment,
    layersData,
    selectedDataTab,
    setSelectedDataTab,
}) => {
    const { pathname } = useLocation();
    const mapService = useMapsService();
    const cifService = useCIFService();
    const setSelBrands = useSetRecoilState(selectedBrands);
    const setSelectedCategory = useSetRecoilState(selectedCategoryState);
    const poiCategories = useRecoilValue(POICategories);
    const poiColors = useRecoilValue(poiMapColorState);
    const topFiveRegions = useRecoilValue(topFiveRegionsState);
    const selectedGeo = useRecoilValue(geoState);
    const expandedPresenceConfig = useRecoilValue(expandedPresenceConfigState);
    const selectedTab = useRecoilValue(selectedTabState);
    const filters = useRecoilValue<any[]>(filtersState);
    const [isListOpen, setIsListOpen] = useState({ categories: false, brands: false, aggLayer: false });
    const [brands, setBrands] = useRecoilState(brandList);
    const [selCategoryList, setSelCategoryList] = useRecoilState(selPOICategories);
    const [cifType, setCifType] = useRecoilState(cifTypeState);
    const [selMetric, setSelMetric] = useRecoilState(metricState);
    const [expandedTopDistricts, setExpandedTopDistricts] = useState(false);
    const [popupPos, setPopupPos] = useState<{ left: any, bottom: any, top: any }>({ left: '0', bottom: 'auto', top: 'auto' });
    const [brandPopupPos, setBrandPopupPos] = useState<{ left: any, bottom: any, top: any }>({ left: '0', bottom: 'auto', top: 'auto' });
    const [prev, setPrev] = useRecoilState(prevCifTypeState);
    const [selAggLayer, setSelAggLayer] = useRecoilState(selAggLayerState);
    const [myBusinessPresenceConfig, setMyBusinessPresenceConfig] = useState<any>([]);
    const [isOpenMetricPopup, setIsOpenMetricPopup] = useState({ EH: false, CTV: false, PoI: false, catchments: false, subregionBoundaries: false });
    const [toggleState, setToggleState] = useState({ MBP: !!isChecked?.MBP, spotlight: !!isChecked?.spotlight, poimarker: !!isChecked?.poimarker, adjacency: !!isChecked?.adjacency, catchments: !!isChecked?.catchments, EH: cifType.includes('EH'), CTV: cifType.includes('CTV'), PoI: cifType.includes('PoI'), subregionBoundaries: !!isChecked?.subregionBoundaries });
    const [aggLayerPopupPos, setAggLayerPopupPos] = useState({ top: 0, left: 0 });
    const setError = useSetRecoilState(errorState);
    const [overlayCount] = useRecoilState<any>(overlayCountState);
    const totalOverlayCount = Object.values(overlayCount || {}).reduce((acc: number, item) => acc + (item as number), 0);
    const [searchParams] = useSearchParams();
    const catchmentId = searchParams.get('catchment')!;
    const [showFilterRegionModal, setShowFilterRegionModal] = useState<boolean>(false);
    const [sliders, setSliders] = useState<any[]>([]);


    const isAggLayerOff = !selAggLayer;

    const catchmentOption = { label: 'Catchment', value: selSubRegion, key: 'catchment' };
    const hexGridOption = { label: 'Hex Grid', value: { key: 'hexgrid', title: 'Hex Grid' }, key: 'hexgrid' };
    const hexLoading = useRecoilValue(hexLoadingState);

   
      const showLoader = selAggLayer?.key=="hexgrid" && hexLoading.loading && !hexLoading.firstChunkReceived;

    const refs = {
        button: useRef<HTMLDivElement>(null),
        brandButton: useRef<HTMLDivElement>(null),
        catCheckBox: useRef<HTMLDivElement>(null),
        brandCheckBox: useRef<HTMLDivElement>(null),
        aggLayerPopup: useRef<HTMLDivElement>(null),
        aggLayerBtn: useRef<HTMLSpanElement>(null),
    };
    const overlayRefs = {
        subregionBoundaries: useRef<HTMLDivElement>(null),
        EH: useRef<HTMLDivElement>(null),
        CTV: useRef<HTMLDivElement>(null),
        PoI: useRef<HTMLDivElement>(null),
        catchments: useRef<HTMLDivElement>(null),
    };
    const subLevel = subGeoLevel(selectedGeo, selectedTab, catchment) as LegendGeoLevel;

    const isTypeDisable = (type: string) => {
        const legend = poiColors?.legendProperties?.[subLevel];
        if (type === 'CTV' && (selectedGeo.district || !legend?.hasData.CTV)) return true;
        if (type === 'EH' && (!legend?.hasData.EH )) return true;
        if (type === 'PoI' && (!selectedGeo.sub_district && !legend?.hasData.PoI)) return true;
        if (type === 'adjacency' && (geoCode === Number(selectedGeo.country) || catchment)) return true;
        if (type === 'subregionBoundaries' && pathname === RouteConstants.root && (isChecked?.catchments || geoCode === Number(selectedGeo.country) || geoCode === Number(selectedGeo.state) || geoCode === Number(selectedGeo.district) || geoCode === Number(selectedGeo.village))) return true;
        if (type === 'catchments' && (catchmentTypes?.length === 0 || !isCatchmentAvailable || pathname === RouteConstants.catchments || geoCode === Number(selectedGeo.village))) return true;
        return false;
    }

    const handleToggleChange = (event: any, options: any) => {
        const { name, checked } = event.target;

        if (name === 'EH' || name === 'CTV' || name === 'PoI') {
            let updated: any = [];
            setCifType(prev => {
                if (checked) updated = [...prev, name];
                else updated = prev.filter(item => item !== name) || [];
                return updated;
            });

            setPrev(prev => {
                let newPrev = checked ? [...prev, name] : prev.filter(item => item !== name) || [];
                localStorage?.setItem("selectedtoggles", JSON.stringify(newPrev));
                return newPrev;
            });

            if (options?.length > 0) {
                setSelMetric(prev => {
                    const updated = { ...prev };
                    const option = prev[name as MetricKey] || options[0];
                    updated[name as MetricKey] = option ? { ...option, isRank: checked } : null;
                    localStorage.setItem("selMetrics", JSON.stringify(updated));
                    return updated;
                });
            }
        } else if (name === 'MBP' || name === 'spotlight' || name === 'subregionBoundaries' || name === 'adjacency' || name === 'catchments' || name === 'poimarker') {
            toggleSwitch(event, name, checked);
            if (name === 'subregionBoundaries' && !checked) {
                setSelAggLayer(hexGridOption.value);
            }
        }
    }

    const handleListOpen = async (keyName: string, tagName?: string) => {
        if (tagName && tagName === 'svg') return;
        if (keyName === 'categories') {
            setIsListOpen({ categories: !isListOpen.categories, brands: false, aggLayer: false });
            if (refs.button.current) {
                const buttonRect = refs.button.current.getBoundingClientRect();
                setTimeout(() => {
                    if (expandedPresenceConfig !== null && expandedPresenceConfig !== undefined) {
                        setPopupPos({ top: 'auto', bottom: window.innerHeight - buttonRect.bottom - 10, left: buttonRect.right + 20 });
                    }
                    else {
                        const popupRef = refs.catCheckBox.current?.getBoundingClientRect();
                        if (popupRef) {
                            setPopupPos({ bottom: 0, left: buttonRect.right + 20, top: 'auto' });
                        } else {
                            setPopupPos({ bottom: 70, left: buttonRect.right + 20, top: 'auto' });
                        }
                    }
                }, 0);
            }
        } else {
            setIsListOpen({ categories: false, brands: !isListOpen.brands, aggLayer: false });
            if (refs.brandButton.current) {
                const buttonRect = refs.brandButton.current.getBoundingClientRect();
                setTimeout(() => {
                    if (expandedPresenceConfig !== null && expandedPresenceConfig !== undefined) {
                        setBrandPopupPos({ top: 'auto', bottom: window.innerHeight - buttonRect.bottom - 10, left: buttonRect.right + 20 });
                    } else {
                        const popupRef = refs.brandCheckBox.current?.getBoundingClientRect();
                        if (popupRef) {
                            setBrandPopupPos({ bottom: 'auto', left: buttonRect.right + 20, top: buttonRect.top - 180 });
                        } else {
                            setBrandPopupPos({ bottom: 70, left: buttonRect.right + 20, top: 'auto' });
                        }
                    }
                }, 0);
            }
        }
    }

    const handleClickOutside = (event: any) => {
        const isOutside = Object.values(overlayRefs).some(
            ref => ref.current && !ref.current.contains(event.target as Node)
        );

        if (isOutside) {
            setIsOpenMetricPopup({ EH: false, CTV: false, PoI: false, catchments: false, subregionBoundaries: false });
        }

        setIsListOpen((prev) => {
            let updatedState = { ...prev };

            if (refs.catCheckBox.current && !refs.catCheckBox.current.contains(event.target)) {
                updatedState.categories = false;
            }
            if (refs.brandCheckBox.current && !refs.brandCheckBox.current.contains(event.target)) {
                updatedState.brands = false;
            }
            if (refs.aggLayerPopup.current && !refs.aggLayerPopup.current.contains(event.target as Node)) {
                updatedState.aggLayer = false;
            }

            return updatedState;
        });
    };

    const handleOpenPopup = (type: any) => {
        // if (isTypeDisable(type)) return;

        setIsOpenMetricPopup(prev => ({
            EH: false,
            CTV: false,
            PoI: false,
            catchments: false,
            subregionBoundaries: false,
            [type]: !prev[type as keyof typeof prev],
        }));
    }

    const handleAggLayerChange = (layer: any) => {
        setSelAggLayer(layer);
        setIsListOpen({ categories: false, brands: false, aggLayer: false });
        localStorage.setItem('agg-layer', JSON.stringify(layer));
    }

    const getPayload = (geoId: number) => {
        return {
            geoId,
            ...selCategoryList,
        }
    }

    // Load from localStorage on mount
    useEffect(() => {
        const savedAggLayer = localStorage.getItem('agg-layer');
        if (savedAggLayer) {
            try {
                const parsed = JSON.parse(savedAggLayer);
                // Only restore hexgrid if it's actually available (when district level or below)
                if (parsed?.key === 'hexgrid' && !selectedGeo.district) {
                    // Hexgrid not available, don't restore it
                    return;
                }
                setSelAggLayer(parsed);
            } catch (e) {
                console.error('Failed to parse agg-layer from localStorage', e);
            }
        }
    }, []);

    useEffect(() => {
        // Only auto-switch if hexgrid is not explicitly selected by user
        const savedAggLayer = localStorage.getItem('agg-layer');
        const isHexgridSelected = savedAggLayer && JSON.parse(savedAggLayer)?.key === 'hexgrid';
        // Check if hexgrid is actually available
        const isHexgridAvailable = selectedGeo.district || selectedGeo.village;

        if (selSubRegion) {
            if (isChecked?.subregionBoundaries) {
                // Only change to subregion if hexgrid is not explicitly selected AND available
                if (!isHexgridSelected || !isHexgridAvailable) {
                    setSelAggLayer(selSubRegion);
                    localStorage.setItem('agg-layer', JSON.stringify(selSubRegion));
                }
            } else {
                // When subregionBoundaries is off, default to hexgrid if available or in catchments view
                if (isHexgridAvailable || pathname === RouteConstants.catchments) {
                    setSelAggLayer(hexGridOption.value);
                    localStorage.setItem('agg-layer', JSON.stringify(hexGridOption.value));
                }
            }
            console.log("REGION", selSubRegion)
        } else if (selectedGeo.village || !isChecked?.subregionBoundaries) {
            setSelAggLayer(hexGridOption.value);
            localStorage.setItem('agg-layer', JSON.stringify(hexGridOption.value));
        }
    }, [selSubRegion, selectedGeo?.village, selectedGeo?.district, catchmentId, isChecked?.subregionBoundaries, pathname]);

    // Reset hex selection to region when geoId, catchment, or adjacency changes
    // useEffect(() => {
    //     // Only reset if hex is currently selected
    //     if (selAggLayer?.key === 'hexgrid' && selSubRegion && isChecked?.subregionBoundaries) {
    //         // Switch to sub-region aggregation
    //         setSelAggLayer(selSubRegion);
    //         localStorage.setItem('agg-layer', JSON.stringify(selSubRegion));
    //     }
    // }, [geoCode, isChecked?.catchments, isChecked?.adjacency]);

    useEffect(() => {
        if (poiCategories?.length === 0) {
            cifService.getPOICategoryList();
            const savedCategories = localStorage.getItem('selCategoryList');
            if (savedCategories) {
                setSelCategoryList(JSON.parse(savedCategories));
            }
        }
    }, []);

    useEffect(() => {
        if (poiCategories?.length > 0) {
            if (selCategoryList?.category?.length === 0) {
                const selCats = {
                    category: poiCategories.map(category => category.key) || [],
                    subcategory: poiCategories
                        .flatMap(category => category.sub_categories?.map(sub => sub.key) || [])
                        .filter((key): key is number => key !== undefined)
                }
                setSelCategoryList(selCats);
                localStorage.setItem('selCategoryList', JSON.stringify(selCats));
            }
            setSelectedCategory(poiCategories[0]); // Set the default to the first category
        }
    }, [poiCategories, selCategoryList?.category]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Prepare brands payload
    const brandsPayload = React.useMemo(() => {
        const shouldFetch = pathname === RouteConstants.root
            && cifType
            && (geoCode === Number(selectedGeo.sub_district) || geoCode === Number(selectedGeo.village))
            && (selCategoryList.category.length > 0 || selCategoryList.subcategory.length > 0);

        if (!shouldFetch) return null;

        const { parent } = getParentGeoId(geoCode, selectedGeo, selectedTab);        
        const filters_new = filters.map(cleanFilterTree);

        return {
            ...getPayload(geoCode),
            parentId: parent,
            adjacency: isChecked?.adjacency || false,
            filters: filters_new,
            catchment: isChecked?.catchments ? [selCatchmentType?.title] : [],
            hex: selectedGeo?.district ? selAggLayer?.key === 'hexgrid' || selAggLayer==null : false,
        };
    }, [pathname, cifType, geoCode, selectedGeo, selectedTab, selCategoryList, filters, isChecked?.adjacency, isChecked?.catchments, selCatchmentType, catchment, selAggLayer?.key]);

    // TanStack Query hook for brands
    const { data: brandsData, isLoading: isBrandsLoading } = useQuery({
        queryKey: ['brands', brandsPayload],
        queryFn: async () => {
            if (!brandsPayload) return null;
            return await mapService.getBrands(brandsPayload);
        },
        enabled: !!brandsPayload,
        staleTime: 5 * 60 * 1000,
    });

    // Update brands state when query data changes (final update)
    useEffect(() => {
        if (brandsData) {
            setBrands(brandsData);
            setSelBrands([]);
        }
    }, [brandsData]);

    // Update map spinner based on loading state
    useEffect(() => {
        if (isBrandsLoading) {
            setMapSpinner(prev => prev + 1);
            return () => setMapSpinner(prev => prev - 1);
        }
    }, [isBrandsLoading]);

    useEffect(() => {
        if (prev.length === 0) return;

        let type = [...prev];

        if (selectedGeo.village && !catchment) {
            type = type.filter(item => (item !== 'CTV')) || [];

            if (prev.includes('PoI') && !type.includes('PoI')) {
                type.push('PoI');
            }
        } else if (selectedGeo.district && !catchment) {
            type = type.filter(item => (item !== 'CTV')) || [];
        }

        if (poiColors?.legendProperties) {
            const legend = poiColors?.legendProperties?.[subLevel];
            if (legend?.hasData) {
                const hasData = legend?.hasData;
                type = type.filter(item => hasData?.[item]) || [];
            }
        }

        setCifType(type);
        const updated = JSON.parse(localStorage.getItem("selMetrics")!) || {};
        setSelMetric(() => {
            Object.entries(updated)?.forEach(([key]) => {
                if (!type.includes(key as CIFType)) {
                    updated[key as CIFType] = { ...updated[key as CIFType], isRank: false };
                } else {
                    updated[key as CIFType] = { ...updated[key as CIFType], isRank: true };
                }
            });
            localStorage.setItem("selMetrics", JSON.stringify(updated));
            return updated;
        });

    }, [poiColors, brands]);

    useEffect(() => {
        setToggleState({
            MBP: isChecked.MBP,
            spotlight: isChecked.spotlight,
            poimarker: isChecked.poimarker,
            adjacency: isChecked.adjacency,
            EH: catchment ? cifType.includes('EH') && (isChecked.subregionBoundaries || isChecked.catchments) : cifType.includes('EH'),
            CTV: cifType.includes('CTV'),
            PoI: cifType.includes('PoI'),
            subregionBoundaries: (isChecked.catchments && !catchment) ? false : isChecked.subregionBoundaries,
            catchments: isChecked.catchments,
        });
        console.log("ISPOI",isChecked)
    }, [isChecked, cifType]);

    useEffect(() => {
        if (isChecked?.catchments && totalOverlayCount >= 1) {
            setError({ type: 'MapInfo', message: `Catchment view does not support filters. Any previously applied filters are not considered here.` });
        }
    }, [isChecked?.catchments]);

    // Get layers based on the new data structure
    const getGenericLayers = () => {
        const genericGroup = layersData.find(group => group.mapping === 'generic');
        return genericGroup?.items || [];
    };

    const getChoroplethLayers = () => {
        const choroplethGroup = layersData.find(group => group.mapping === 'choropleth');
        return choroplethGroup?.items || [];
    };

    const getMarkersLayers = () => {
        const markersGroup = layersData.find(group => group.mapping === 'markers');
        return markersGroup?.items || [];
    };

    const getDataLayersGroup = () => {
        const layersGroup = layersData.find(group => group.mapping === 'layers');
        return layersGroup?.items || [];
    };

    const renderLayer = (layer: any, isChoropleth = false): JSX.Element => {
        const isToggled = toggleState[layer.type as keyof typeof toggleState];
        const isChoroplethMetric = ['EH', 'CTV', 'PoI'].includes(layer.type);
        const disabled = (isChoropleth && isChoroplethMetric && isAggLayerOff) || (layer.type === "MBP" ? !layer.hasmbp : isTypeDisable(layer.type as CIFType));

        return (
            <React.Fragment key={layer.type}>
                <div className={`d-flex flex-column padding-left-4 padding-right-4 padding-bottom-3 padding-top-3 position-relative m-0  gap-1 ${(layer.type === 'MBP' || layer.type === 'data-layers' || layer.type === 'PoI') ? 'row' : ''} ${layer.type === 'subregionBoundaries' && 'flex-wrap'}`} >
                    <CifTypeSwitch
                        layer={layer}
                        isChecked={isToggled}
                        cifType={cifType}
                        handleToggleChange={(e) => handleToggleChange(e, layer.options)}
                        disabled={disabled}
                        selMetric={selMetric}
                        isPoiOverlapped={isPoiOverlapped}
                        catchment={catchment}
                        customTooltip={layer.type === 'catchments' && disabled && !isToggled ? !isCatchmentAvailable ? "No catchment available in this area. Catchment has been turned off." : 'We have not received your business catchment data to visualise' : layer?.type === "MBP" ? "No business presence data available." : ""}
                    />
                    {layer.type === 'MBP' &&
                        <PresenceConfig
                            isChecked={isChecked}
                            toggleSwitch={toggleSwitch}
                            expandedTopDistricts={expandedTopDistricts}
                            setExpandedTopDistricts={setExpandedTopDistricts}
                            fetchMyPresence={fetchMyPresence}
                            setMbpFilter={setMbpFilter}
                            presenceTypeCount={presenceTypeCount}
                            mapSpinner={mapSpinner}
                            myPresence={myPresence}
                            myBusinessPresenceConfig={myBusinessPresenceConfig}
                            setMyBusinessPresenceConfig={setMyBusinessPresenceConfig}
                        />
                    }
                    {(layer.type === 'EH' || layer.type === 'CTV' || layer.type === 'PoI' || layer.type === 'catchments' || ((layer.type === 'subregionBoundaries' && !selectedGeo.village) || (catchment && layer.type === 'subregionBoundaries'))) &&
                        (() => {
                            const getMetricsForLayer = () => {
                                if (!isToggled) return null;

                                if (layer.type === 'subregionBoundaries') {
                                    if (catchment) return null;
                                    return BOUNDARY_TYPES[getCurrGeoLevelName(selectedGeo) as keyof typeof BOUNDARY_TYPES];
                                }
                                if (layer.type === 'catchments') {
                                    if (pathname === RouteConstants.catchments) return null;
                                    return catchmentTypes;
                                }
                                return layer.options;
                            };

                            const getSelectedMetric = () => {
                                if (!isToggled) return null;

                                if (layer.type === 'subregionBoundaries') return selSubRegion;
                                if (layer.type === 'catchments') return selCatchmentType;
                                return selMetric[layer.type as MetricKey];
                            };

                            const getMetricSetter = () => {
                                if (!isToggled) return null;

                                if (layer.type === 'subregionBoundaries') return setSelSubRegion;
                                if (layer.type === 'catchments') return setSelCatchmentType;
                                return setSelMetric;
                            };

                            return (
                                <OverlayMetricPopup
                                    type={layer.type}
                                    isChecked={isToggled}
                                    isDisabled={disabled}
                                    isOpen={isOpenMetricPopup[layer.type as keyof typeof isOpenMetricPopup] || false}
                                    handleOpen={() => handleOpenPopup(layer.type as any)}
                                    metrics={getMetricsForLayer()}
                                    selMetric={getSelectedMetric()}
                                    setSelMetric={getMetricSetter()}
                                    ref={overlayRefs[layer.type as keyof typeof overlayRefs] || undefined}
                                    infoText={getLayerInfoText(layer.type, disabled, isToggled, isChecked, geoCode, selectedGeo, selCatchmentType, selMetric, selSubRegion, catchment) || null}
                                />
                            );
                        })()
                    }
                    {((layer.type === 'poimarker' && isChecked?.poimarker) || (layer?.type == "PoI" && cifType.includes('PoI'))) && (
                        <POICategoryListHome
                            isOpen={isListOpen.categories}
                            handleListOpen={handleListOpen}
                            catCheckBoxRef={refs.catCheckBox}
                            buttonRef={refs.button}
                            isListOpen={isListOpen}
                            catchment={catchment}
                            isAggLayerOff={isAggLayerOff}
                        />
                    )}
                    {layer.type === 'poimarker' && isChecked?.poimarker &&
                        <BrandWatchList
                            isOpen={isListOpen.brands}
                            handleListOpen={handleListOpen}
                            brandCheckBoxRef={refs.brandCheckBox}
                            brandButtonRef={refs.brandButton}
                            style={{
                                left: brandPopupPos.left,
                                bottom: 0,
                            }}
                        />
                    }
                </div>
                {/* <hr className={`p-0 m-0 ${(layer.type === 'data-layers' || layer.type === 'EH' || layer.type === 'CTV') ? 'margin-left-right-5 border-dotted' : ''}`} /> */}
                {layer.type === 'PoI' && isPoiOverlapped &&
                    <Body
                        type={BodyType.p3}
                        color={BodyColor.primary}
                        classname="padding-4 fs-9 italics"
                    >
                        * PoIs are only partially covered for this {catchment ? "Catchment" : selectedGeo?.village ? "Village" : "PIN Code"}
                    </Body>
                }
            </React.Fragment>
        );
    };

    const genericLayers = getGenericLayers();
    const choroplethLayers = getChoroplethLayers();
    const markersLayers = getMarkersLayers();
    const dataLayersGroup = getDataLayersGroup();

    return (
        <div className="left-sidebar position-relative h-100 d-flex flex-column justify-content-between">
            <div
                className="overflow-x-hidden overflow-y-auto no-scrollbar d-flex flex-column"
                style={{ height: expandedTopDistricts && (cifType.toString() === 'CTV' || cifType.toString() === 'PoI') ? "85%" : "90%", flexGrow: selectedGeo.sub_district || selectedGeo.district ? 1 : 0 }}
            >
                {/* Generic Layers (Catchments, Sub-region Boundaries, Adjacency) */}
                {genericLayers
                    .filter((layer: any) => !(
                        (layer.type === 'subregionBoundaries' && selectedGeo.village && !catchment) ||
                        (layer.type === 'adjacency' && geoCode === Number(selectedGeo.country) && pathname === RouteConstants.root) ||
                        (layer.type === 'catchments' && (geoCode === Number(selectedGeo.village)))
                    ))
                    .map(renderLayer)
                }

                {/* Tabs for Choropleth and Markers */}
                {(
                    <>
                        <div className="d-flex gap-2 border-top-2 padding-left-4 padding-right-4 w-100">
                            {DATA_TABS.map(tab => (
                                <div
                                    key={tab.key}
                                    className="d-flex align-items-center gap-2 cursor-pointer justify-content-center"
                                    onClick={() => setSelectedDataTab(tab.key)}
                                >
                                    <img
                                        src={selectedDataTab === tab.key ? tab.activeIcon : tab.inactiveIcon}
                                        alt={tab.alt}
                                        style={{ width: 50, height: 50 }}
                                    />
                                </div>
                            ))}
                        </div>
                        <hr className="p-0 m-0" />
                    </>
                )}

                {/* Choropleth Layers */}
                <div
                    className={`${styles.tabContent} ${selectedDataTab === 'choropleth' ? styles.tabContentActive : ''}`}
                    style={{ display: selectedDataTab === 'choropleth' ? 'block' : 'none' }}
                >
                    <div className="d-flex align-items-center justify-content-between padding-left-4 padding-right-4 padding-bottom-3 padding-top-3">
                        <Body
                            type={BodyType.p3}
                            color={BodyColor.primary}
                            classname="p-0 m-0"
                        >
                            {selectedDataTab?.toUpperCase()}
                        </Body>
                        {((!selectedGeo?.village && selSubRegion?.title) || selectedGeo.village) && (
                            <div className="d-flex align-items-center gap-1 bg-black-17 rounded padding-left-right-2 padding-top-bottom-1">
                                <Heading
                                    title={selAggLayer ? (isChecked?.catchments && !catchmentId && selAggLayer?.key !== 'hexgrid' ? 'CATCHMENT' : selAggLayer.title.toUpperCase()) : 'OFF'}
                                    type={TypographyType.h6}
                                    colour={TypographyColor.secondary}
                                    classname='p-0 m-0'
                                />
                                <span
                                    ref={refs.aggLayerBtn}
                                    className="material-symbols-outlined fs-18 cursor-pointer"
                                    onClick={(e) => {
                                        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                        setAggLayerPopupPos({ top: rect.top, left: rect.right + 4 });
                                        setIsListOpen(prev => ({ ...prev, aggLayer: !prev.aggLayer }));
                                    }}
                                >
                                    keyboard_arrow_right
                                </span>
                                {showLoader && (
                                    <span className="spinner-border spinner-border-sm" role="status" aria-label="Loading hex data">
                                        <span className="visually-hidden">Loading...</span>
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    {(pathname === RouteConstants.root || pathname === RouteConstants.catchments) &&
                        <FilterRegionModal
                            showFilterRegionModal={showFilterRegionModal}
                            setShowFilterRegionModal={setShowFilterRegionModal}
                            sliders={sliders} setSliders={setSliders}
                        />
                    }
                    {choroplethLayers.map((layer: any) => renderLayer(layer, true))}
                </div>

                {/* Markers Layers */}
              
                <div
                    className={`${styles.tabContent} ${selectedDataTab === 'markers' ? styles.tabContentActive : ''}`}
                    style={{ display: selectedDataTab === 'markers' ? 'block' : 'none' }}
                >
                    <Body
                        type={BodyType.p3}
                        color={BodyColor.primary}
                        classname="d-flex flex-column padding-left-4 padding-right-4 padding-bottom-3 padding-top-3"
                    >
                        {selectedDataTab?.toUpperCase()}
                    </Body>
                    {markersLayers?.filter((layer: any) => layer.type !== 'poimarker' || (catchment || selectedGeo.sub_district)).map(renderLayer)}
                </div>
            </div>
                

            <TopDistricts
                cifType={cifType}
                expandedTopDistricts={expandedTopDistricts}
                setExpandedTopDistricts={setExpandedTopDistricts}
                topfiveregions={Array.isArray(topFiveRegions) ? topFiveRegions : []}
                selCatchmentType={selCatchmentType}
                dataLayersGroup={dataLayersGroup}
                legendShimmer={legendShimmer}
                geoCode={geoCode}
                isPoiOverlapped={isPoiOverlapped}
            />

            {isListOpen.aggLayer && ((!selectedGeo.village && selSubRegion?.title) || selectedGeo.village) && (
                <AggLayerPopup
                    ref={refs.aggLayerPopup}
                    options={[
                        ...((isChecked?.catchments && !catchmentId)
                            ? [catchmentOption]
                            : (((!selectedGeo.village || (selectedGeo?.village && catchment))) && isChecked?.subregionBoundaries) ? [{ label: selSubRegion.title, value: selSubRegion, key: 'subregion' }] : []),
                        ...(((!selectedGeo.village && selSubRegion?.key >= 4) || selectedGeo.village) ? [hexGridOption] : []),
                        { label: 'OFF', value: null, key: 'off' },
                    ]}
                    selectedKey={!selAggLayer ? 'off' : selAggLayer?.key === 'hexgrid' ? 'hexgrid' : isChecked?.catchments && !catchmentId ? 'catchment' : 'subregion'}
                    position={aggLayerPopupPos}
                    onSelect={(value) => handleAggLayerChange(value)}
                />
            )}
        </div >
    )
}
